import React, { useEffect, useState } from 'react';
import { Button, notification } from 'antd';
import {
    X,
    CheckCircle,
    Circle,
    User,
    Lightning,
    Buildings,
    AddressBook,
    FileText,
    CreditCard,
    ArrowsClockwise,
} from '@phosphor-icons/react';
import { useOneButtonStore, type PartnerSyncChecklist } from '../../../../store/oneButtonStore';
import {
    getPartnerSyncConfig,
    getStepLabel,
    findSyncStatusRowForLeadOrProduct,
    financePartnerSyncStoreKey,
    checklistFromFinancePartnerSyncRow,
    partnerSkipsTitleCheck,
    type PartnerSyncStepKey,
} from './partnerSyncConfig';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import {
    invalidateRetrievedOpportunityDetails,
    withFinancePartnerLeadList,
} from '../../../../helpers/lightReachLeadId';
import FinancePartnerCreateLeadProgressModal from './FinancePartnerCreateLeadProgressModal';
import CreditCheckModal from './CreditCheckModal';
import './oneButton-css/FinancePartnerSyncModal.css';
import { OBD_FINANCE_PARTNER_SYNC_Z } from './oneButtonOverlayZ';
import DashboardModal from './DashboardModal';

// --- Credit check row visibility (opportunity detail + lead list) ---
function opportunityDetailsNested(od: any) {
    return od?.OpportunityDetails ?? od?.opportunityDetails;
}

function getOpportunityProductRows(od: any): any[] {
    if (!od) return [];
    const n = opportunityDetailsNested(od);
    const pick = od.OpportunityProduct ?? od.opportunityProduct ?? n?.OpportunityProduct ?? n?.opportunityProduct;
    return Array.isArray(pick) ? pick : [];
}

function getFinancePartnerLeadList(od: any): any[] {
    if (!od) return [];
    const nested = opportunityDetailsNested(od);
    const list =
        od.FinancePartnerLeadList ??
        od.financePartnerLeadListModels ??
        od.financePartnerLeadList ??
        nested?.FinancePartnerLeadList ??
        nested?.financePartnerLeadListModels ??
        nested?.financePartnerLeadList ??
        [];
    return Array.isArray(list) ? list : [];
}

function hasNonEmptyId(v: any): boolean {
    return v != null && String(v).trim() !== '';
}

function leadIdFromRecord(row: any): string {
    return String(
        row?.Finance_Partner_Sync_LeadID ??
            row?.Finance_Partner_Sync_LeadId ??
            row?.FinancePartnerSyncLeadId ??
            row?.finance_Partner_Sync_LeadID ??
            row?.finance_Partner_Lead_Sync_Id ??
            '',
    ).trim();
}

function hasFinancePartnerSyncLead(od: any, leadList: any[]): boolean {
    const n = opportunityDetailsNested(od);
    const main = od?.Finance_Partner_Sync_LeadID ?? n?.Finance_Partner_Sync_LeadID ?? od?.finance_Partner_Sync_LeadID;
    const co = od?.Finance_Partner_Sync_CO_LeadID ?? n?.Finance_Partner_Sync_CO_LeadID ?? od?.finance_Partner_Sync_CO_LeadID;
    if (hasNonEmptyId(main) || hasNonEmptyId(co)) return true;
    return leadList.some((row: any) => hasNonEmptyId(leadIdFromRecord(row)));
}

function partnerNameFromOpportunityProduct(row: any): string {
    return String(
        row?.Finance_Partner_Display_Name ??
            row?.FinancePartnerDisplayName ??
            row?.Finance_Partner ??
            row?.finance_Partner ??
            row?.Finance_Partner_Code ??
            '',
    ).trim();
}

/** Match checklist `partnerName` (e.g. LightReach, IGS) to opportunity product rows */
function toCanonicalPartnerKeyFromProductRow(raw: string): string {
    const k = String(raw || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
    if (!k) return '';
    if (k.includes('lightreach')) return 'LightReach';
    if (k.includes('goodleap') || (k.includes('good') && k.includes('leap'))) return 'GoodLeap';
    if (k.startsWith('igs')) return 'IGS';
    if (k.includes('cash')) return 'Cash';
    if (k.includes('enfin')) return 'Enfin';
    if (k.includes('sunnova')) return 'Sunnova';
    if (k.includes('sunlight')) return 'Sunlight';
    return String(raw).trim();
}

function partnerNameFromLeadRecord(row: any): string {
    return String(
        row?.FinancePartnerName ??
            row?.Finance_Partner_Name ??
            row?.Finance_Partner ??
            row?.financePartnerName ??
            row?.FinancePartnerName2 ??
            '',
    ).trim();
}

function leadRecordMatchesPartner(row: any, partnerName: string): boolean {
    const nm = partnerNameFromLeadRecord(row);
    if (!nm) return false;
    return (
        toCanonicalPartnerKeyFromProductRow(nm) ===
        toCanonicalPartnerKeyFromProductRow(partnerName)
    );
}

function leadRecordProductType(row: any): string {
    return String(
        row?.ProductTypeName ??
            row?.productTypeName ??
            row?.Product_Type_Name ??
            row?.lead_Type ??
            row?.Lead_Type ??
            '',
    ).trim();
}

function normalizeProductTypeKey(type: string | null | undefined): string {
    return String(type || '')
        .trim()
        .toLowerCase()
        .replace(/\s+only$/, '');
}

function leadRecordMatchesProductType(row: any, productName: string): boolean {
    const selected = normalizeProductTypeKey(productName);
    if (!selected) return true;
    const leadType = normalizeProductTypeKey(leadRecordProductType(row));
    if (!leadType) return false;
    return leadType === selected || leadType.includes(selected) || selected.includes(leadType);
}

function leadRecordTimestamp(row: any): number {
    const raw =
        row?.LastModifiedDate ??
        row?.lastModifiedDate ??
        row?.CreatedDate ??
        row?.createdDate ??
        row?.CreatedOn ??
        row?.ModifiedDate ??
        '';
    const parsed = Date.parse(String(raw));
    return Number.isFinite(parsed) ? parsed : 0;
}

/** Newest lead in the list: latest date when present, otherwise the last matching row. */
function pickLatestLead(rows: any[]): any | null {
    const withIds = rows.filter((row: any) => hasNonEmptyId(leadIdFromRecord(row)));
    if (withIds.length === 0) return null;
    const dated = withIds.filter((row: any) => leadRecordTimestamp(row) > 0);
    if (dated.length > 0) {
        return dated.reduce((latest: any, row: any) =>
            leadRecordTimestamp(row) >= leadRecordTimestamp(latest) ? row : latest,
        );
    }
    return withIds[withIds.length - 1];
}

/** Current finance-partner Lead Id for this partner (and product, when provided). */
function getSyncedLeadIdForPartner(od: any, partnerName: string, productName?: string): string | null {
    if (!od) return null;
    const partnerKey = toCanonicalPartnerKeyFromProductRow(partnerName);
    const selectedProduct = String(productName || '').trim();
    const leadList = getFinancePartnerLeadList(od);

    let matchingLeads = leadList.filter((row: any) => leadRecordMatchesPartner(row, partnerName));
    if (selectedProduct) {
        matchingLeads = matchingLeads.filter((row: any) => leadRecordMatchesProductType(row, selectedProduct));
    }

    const latest = pickLatestLead(matchingLeads);
    if (latest) return leadIdFromRecord(latest);

    for (const row of getOpportunityProductRows(od)) {
        if (row?.ActiveFlag === false || row?.activeFlag === false) continue;
        if (toCanonicalPartnerKeyFromProductRow(partnerNameFromOpportunityProduct(row)) !== partnerKey) continue;
        if (selectedProduct) {
            const rowType = String(row.Product_Type_Name ?? row.product_Type_Name ?? row.ProductTypeName ?? '').trim();
            if (!leadRecordMatchesProductType({ ProductTypeName: rowType }, selectedProduct)) continue;
        }
        const id = leadIdFromRecord(row);
        if (id) return id;
    }

    return null;
}

function hasFinancePartnerSyncLeadForPartner(od: any, partnerName: string, productName?: string): boolean {
    return getSyncedLeadIdForPartner(od, partnerName, productName) != null;
}

/** Supported partners for credit: Sunnova, IGS, Lightreach, Sunlight (by display / code); IGS company-style names allowed. */
function isValidFinancePartnerRow(name: string | null | undefined): boolean {
    const s = String(name ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
    if (!s) return false;
    if (s === 'igs' || s.startsWith('igssolar')) return true;
    if (s.includes('sunnova')) return true;
    if (s.includes('lightreach')) return true;
    if (s.includes('sunlight')) return true;
    return false;
}

/** Active opportunity products that use this finance partner (for modal title). */
function getActiveProductTypeNamesForSyncPartner(od: any, partnerName: string): string[] {
    const rows = getOpportunityProductRows(od);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const row of rows) {
        if (row?.ActiveFlag === false || row?.activeFlag === false) continue;
        const raw = partnerNameFromOpportunityProduct(row);
        if (!raw) continue;
        if (toCanonicalPartnerKeyFromProductRow(raw) !== toCanonicalPartnerKeyFromProductRow(partnerName)) continue;
        const pt = String(row.Product_Type_Name ?? row.product_Type_Name ?? '').trim();
        if (pt && !seen.has(pt)) {
            seen.add(pt);
            out.push(pt);
        }
    }
    return out;
}

/**
 * Whether to show the Credit check step: no customer has passed credit, a sync lead id exists, and
 * `OpportunityProduct` or `FinancePartnerLeadList` includes a supported partner. Does not use
 * `productTypeList` / `financePartnersList` from the store (rely on opportunity payload).
 */
const CREDIT_CHECK_UNSAVED_CONTACTS_MESSAGE =
    'Unsaved contacts for credit check. Please save them before proceeding with the credit check.';

function firstOpportunityProductForCreditGuard(od: any): any | null {
    const n = od?.OpportunityDetails ?? od?.opportunityDetails;
    const list = od?.OpportunityProduct ?? od?.opportunityProduct ?? n?.OpportunityProduct ?? n?.opportunityProduct;
    return Array.isArray(list) && list.length > 0 ? list[0] : null;
}

function isPrimaryOpportunityProductLightreachForCreditGuard(od: any): boolean {
    const p = firstOpportunityProductForCreditGuard(od);
    const raw = String(p?.Finance_Partner ?? p?.Finance_Partner_Display_Name ?? '')
        .replace(/\s/g, '')
        .toLowerCase();
    return raw.includes('lightreach');
}

/** Block credit check when opportunity snapshot is not ready (aligned with customer-info credit gate). */
function canOpenCreditCheckFromChecklist(opportunityDetails: any): boolean {
    if (!opportunityDetails) return false;
    if (isPrimaryOpportunityProductLightreachForCreditGuard(opportunityDetails)) return true;
    const customers = opportunityDetails.Customers ?? opportunityDetails.customers ?? [];
    if (!Array.isArray(customers)) return true;
    for (const c of customers) {
        const syncId = c?.Finance_Partner_Sync_ContactId ?? c?.finance_Partner_Sync_ContactId;
        const hasSync = syncId != null && String(syncId).trim() !== '';
        const role =
            !!(c?.IsPrimaryContact ||
                c?.isPrimaryContact ||
                c?.IsSecondaryContact ||
                c?.isSecondaryContact ||
                c?.AddToContract ||
                c?.addToContract ||
                c?.IsHomeOwner ||
                c?.IsHomeowner ||
                c?.isHomeowner);
        if (role && !hasSync) return false;
    }
    return true;
}

function shouldShowCreditCheckStep(opportunityDetails: any): boolean {
    if (!opportunityDetails) return false;
    if (opportunityDetails.IsAnyCreditPassed === true || opportunityDetails.isAnyCreditPassed === true) return false;

    const customers = opportunityDetails.Customers ?? opportunityDetails.customers ?? [];
    if (Array.isArray(customers) && customers.some((c: any) => c?.CreditCheckStatus === true)) return false;

    const leadList = getFinancePartnerLeadList(opportunityDetails);
    if (!hasFinancePartnerSyncLead(opportunityDetails, leadList)) return false;

    const products = getOpportunityProductRows(opportunityDetails);
    for (const el of products) {
        if (el?.ActiveFlag === false) continue;
        if (isValidFinancePartnerRow(partnerNameFromOpportunityProduct(el))) return true;
    }
    for (const row of leadList) {
        const nm = String(row?.FinancePartnerName ?? row?.Finance_Partner_Name ?? row?.FinancePartnerName2 ?? '').trim();
        if (isValidFinancePartnerRow(nm)) return true;
    }
    return false;
}

function hasAnyCreditCheckRun(opportunityDetails: any): boolean {
    if (!opportunityDetails) return false;
    if (opportunityDetails.IsAnyCreditPassed === true || opportunityDetails.isAnyCreditPassed === true) return true;
    const customers = opportunityDetails.Customers ?? opportunityDetails.customers ?? [];
    if (!Array.isArray(customers)) return false;
    return customers.some((c: any) => c?.CreditCheckStatus === true || c?.creditCheckStatus === true);
}

const STEP_ICONS: Record<PartnerSyncStepKey, React.ReactNode> = {
    leadSystem: <Buildings size={18} weight="duotone" aria-hidden />,
    utility: <Lightning size={18} weight="duotone" aria-hidden />,
    contact: <AddressBook size={18} weight="duotone" aria-hidden />,
    titleCheck: <FileText size={18} weight="duotone" aria-hidden />,
    creditCheck: <CreditCard size={18} weight="duotone" aria-hidden />,
};

const AUTOMATION_STEPS: PartnerSyncStepKey[] = ['utility', 'leadSystem'];
const ADDITIONAL_STEPS: PartnerSyncStepKey[] = ['contact', 'titleCheck', 'creditCheck'];

export interface FinancePartnerChecklistModalProps {
    open: boolean;
    partnerName: string;
    /**
     * Selected product line when opened from Products hub Partner Sync (e.g. Solar, Battery).
     * When set, title and progress sync `productsList` target that product.
     */
    productName?: string;
    /**
     * When true, show selected product types in the modal title (e.g. "(Solar, Battery)").
     * This is intended only for the Finance Partner Sync card experience, not Products hub.
     */
    showProductTypesInTitle?: boolean;
    onClose: () => void;
}

/** Modal title: internal keys (e.g. GoodLeap) → user-facing partner label */
function financePartnerSyncTitleSuffix(partnerName: string): string {
    const u = partnerName.toUpperCase();
    if (u === 'GOODLEAP') return 'Good Leap';
    if (u === 'LIGHTREACH') return 'Lightreach';
    return partnerName;
}

const DEFAULT_STATUS: PartnerSyncChecklist = {
    leadSyncAction: null,
    lead: false,
    leadSystem: false,
    utility: false,
    contact: false,
    titleCheck: false,
    creditCheck: false,
};

export const FinancePartnerChecklistModal: React.FC<FinancePartnerChecklistModalProps> = ({
    open,
    partnerName,
    productName,
    showProductTypesInTitle = false,
    onClose,
}) => {
    const theme = useOneButtonStore((s) => s.theme);
    const opportunityId = useOneButtonStore((s) => s.opportunityID);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const syncStatusStoreKey = financePartnerSyncStoreKey(partnerName, productName);
    const storeSyncStatus = useOneButtonStore(
        (s) => s.financePartnerSyncStatus[syncStatusStoreKey] ?? s.financePartnerSyncStatus[partnerName],
    );
    const setSyncStatus = useOneButtonStore((s) => s.setFinancePartnerSyncStatus);
    const rawStatuses = useOneButtonStore((s) => s.financePartnerRawSyncStatuses);
    const isBackgroundSyncing = useOneButtonStore((s) => s.isBackgroundSyncing);
    const [progressOpen, setProgressOpen] = useState(false);
    const [resyncStepId, setResyncStepId] = useState<string | null>(null);
    const [progressAction, setProgressAction] = useState<'create' | 'sync' | 'automation_sync'>('automation_sync');
    const [isBusy, setIsBusy] = useState(false);
    const [busyText, setBusyText] = useState('Loading Sync Status...');  
    
    const showBusy = isBusy || isBackgroundSyncing;
    const finalBusyText = isBackgroundSyncing ? 'Refreshing Data...' : busyText;  

    const [noticeAPI, noticeContextHolder] = notification.useNotification();

    const fetchStatus = async () => {
        if (!opportunityId) return;
        setIsBusy(true);
        try {
            const res = await quoteServices.getSyncStatusList(String(opportunityId));
            const syncList = res?.FinancePartnerSyncStatuses || (Array.isArray(res) ? res : []);
            if (Array.isArray(syncList) && syncList.length > 0) {
                useOneButtonStore.getState().setFinancePartnerRawSyncStatuses(syncList);
            }
        } catch (err) {
            console.error("Failed to fetch sync status", err);
        } finally {
            setIsBusy(false);
        }
    };

    const syncedLeadId = React.useMemo(
        () => getSyncedLeadIdForPartner(opportunityDetails, partnerName, productName),
        [opportunityDetails, partnerName, productName],
    );

    const status: PartnerSyncChecklist = React.useMemo(() => {
        const matchingRow = findSyncStatusRowForLeadOrProduct(rawStatuses, {
            leadId: syncedLeadId,
            productTypeName: productName,
        });
        const row =
            matchingRow ??
            (!productName && Array.isArray(rawStatuses) && rawStatuses.length > 0 ? rawStatuses[0] : undefined);
        const fromRow = checklistFromFinancePartnerSyncRow(row);
        const fromStore = storeSyncStatus ?? {};
        return {
            ...DEFAULT_STATUS,
            ...fromStore,
            ...fromRow,
            leadSyncAction: fromStore.leadSyncAction ?? null,
            lead: fromRow.lead === true || !!syncedLeadId,
            contact: fromRow.contact === true || fromStore.contact === true,
            utility: fromRow.utility === true || fromStore.utility === true,
            leadSystem: fromRow.leadSystem === true || fromStore.leadSystem === true,
            titleCheck: fromRow.titleCheck === true || fromStore.titleCheck === true,
            creditCheck: fromRow.creditCheck === true || fromStore.creditCheck === true,
        };
    }, [rawStatuses, syncedLeadId, productName, storeSyncStatus]);

    const handleOpenProgress = (stepId?: string) => {
        const up = partnerName.toUpperCase();
        if (up !== 'IGS' && up !== 'LIGHTREACH') return;

        const od = useOneButtonStore.getState().opportunityDetails;
        const leadId = getSyncedLeadIdForPartner(od, partnerName, productName);
        const hasLeadId = leadId != null && String(leadId).trim() !== '';

        const isLeadCreated = status.lead === true || hasLeadId;

        if (!isLeadCreated) {
            noticeAPI.warning({
                message: 'Validation Error',
                description: `A lead must be created for ${partnerName} before proceeding.`,
                placement: 'topRight',
                className: `obd-notification obd-theme-${theme}`,
                style: { zIndex: 10000 },
            });
            return;
        }

        setResyncStepId(stepId || null);
        setProgressAction('automation_sync');
        setProgressOpen(true);
    };

    const config = getPartnerSyncConfig(partnerName);
    const setLeadSyncFormOpen = useOneButtonStore((s) => s.setLeadSyncFormOpen);
    const setIgsLeadSyncModalOpen = useOneButtonStore((s) => s.setIgsLeadSyncModalOpen);
    const setCreditCheckModalOpen = useOneButtonStore((s) => s.setCreditCheckModalOpen);

    // Fetch initial sync status from API when modal opens (for IGS or others that support it)
    useEffect(() => {
        const upName = partnerName.toUpperCase();
        if (open && (upName === 'IGS' || upName === 'LIGHTREACH') && opportunityId) {
            fetchStatus();
        }
    }, [open, partnerName, opportunityId]);

    /** Success-only status fetch (no busy UI) to refresh checklist icons after creation */
    const refreshSyncStatus = async (showBusy = true) => {
        if (!opportunityId) return;
        if (showBusy) setIsBusy(true);
        try {
            const res = await quoteServices.getSyncStatusList(String(opportunityId));
            const syncList = res?.FinancePartnerSyncStatuses || res || [];
            if (Array.isArray(syncList)) {
                useOneButtonStore.getState().setFinancePartnerRawSyncStatuses(syncList);
            }
        } catch (e) {
            console.error("Failed to refresh status", e);
        } finally {
            if (showBusy) setIsBusy(false);
        }
    };

    const refreshOpportunityDetails = async (showBusy = false) => {
        const state = useOneButtonStore.getState();
        const salesforceId = state.opportunityDetails?.SalesForce_Id || state.opportunityDetails?.Salesforce_Id || opportunityId;
        const numericOppId =
            state.opportunityDetails?.Opportunity_Id ??
            state.opportunityDetails?.opportunity_Id ??
            opportunityId;
        if (!salesforceId) return;

        if (showBusy) {
            setIsBusy(true);
            setBusyText('Refreshing Opportunity Details...');
        }
        try {
            invalidateRetrievedOpportunityDetails(numericOppId);
            const retrieved =
                numericOppId != null && String(numericOppId).trim() !== ''
                    ? await opportunityInfoServices
                          .retrieveOpportunityDetails(String(numericOppId))
                          .catch(() => null)
                    : null;
            const oppDetail = await opportunityInfoServices.getOpportunityDetail(String(salesforceId));
            const base = oppDetail ?? state.opportunityDetails;
            const merged = withFinancePartnerLeadList(base, retrieved);
            if (merged) state.setOpportunityDetails(merged);
            else if (oppDetail) state.setOpportunityDetails(oppDetail);
        } catch (e) {
            console.error("Failed to refresh opportunity details", e);
        } finally {
            if (showBusy) {
                setIsBusy(false);
                setBusyText('Loading Sync Status...');
            }
        }
    };

    const handleOpenLeadSyncForm = () => {
        setSyncStatus(partnerName, { leadSyncAction: 'sync_existing' }, productName);
        if (partnerName.toUpperCase() === 'IGS') {
            setIgsLeadSyncModalOpen(true, productName);
        } else {
            setLeadSyncFormOpen(true, 'sync_existing', partnerName, { productName });
        }
    };

    const toggle = (key: keyof Omit<PartnerSyncChecklist, 'leadSyncAction'>) => {
        // If it's already successful, we don't want to allow unselecting it.
        if (effectiveStatus[key]) return;

        const upName = partnerName.toUpperCase();
        const autoTriggerSteps = ['utility', 'leadSystem', 'contact', 'titleCheck'];
        const manualTriggerSteps = ['creditCheck'];

        // For IGS/Lightreach, trigger the automated sequence for specific steps if not already success
        if ((upName === 'IGS' || upName === 'LIGHTREACH') && autoTriggerSteps.includes(key)) {
            handleOpenProgress(key); 
            return;
        }

        if ((upName === 'IGS' || upName === 'LIGHTREACH' || upName === 'SUNNOVA') && manualTriggerSteps.includes(key)) {
            if (key === 'creditCheck') {
                // Credit already ran: do not reopen (Angular hides the action). LightReach product
                // rows use IsCreditPass instead, so Battery can still open after Solar has passed.
                if (creditAlreadyRun && !(productName && partnerName.toUpperCase() === 'LIGHTREACH')) return;
                const od = useOneButtonStore.getState().opportunityDetails;

                const leadId = getSyncedLeadIdForPartner(od, partnerName, productName);
                const hasLeadId = leadId != null && String(leadId).trim() !== '';
                if (!hasLeadId) {
                    noticeAPI.info({
                        message: 'Lead not synced. Please save/sync lead before proceeding with the credit check.',
                        placement: 'topRight',
                        className: `obd-notification obd-theme-${theme}`,
                        style: { zIndex: 10000 },
                    });
                    return;
                }

                if (!canOpenCreditCheckFromChecklist(od)) {
                    noticeAPI.info({
                        message: CREDIT_CHECK_UNSAVED_CONTACTS_MESSAGE,
                        placement: 'topRight',
                        className: `obd-notification obd-theme-${theme}`,
                        style: { zIndex: 10000 },
                    });
                    return;
                }
            }
            setCreditCheckModalOpen(true, partnerName);
            return;
        }

        setSyncStatus(partnerName, { [key]: true }, productName);
    };

    const productsList = React.useMemo(() => {
        const list: any[] = [];
        const allProducts = getOpportunityProductRows(opportunityDetails);
        const selected = String(productName || '').trim();

        allProducts.forEach((p: any) => {
            const rowProductName = String(
                p.Product_Type_Name ?? p.product_Type_Name ?? p.ProductType ?? p.ProductCode ?? '',
            ).trim();
            if (selected && rowProductName !== selected) return;

            let type = p.ProductCode || p.ProductType || p.Product_Type_Name || selected || 'Solar';
            if (type === 'Battery') type = 'Battery Only';
            const category = p.ProductCategory || p.Product_Category || type;
            if (!list.find((ps) => ps.productType === type)) {
                list.push({ productType: type, category });
            }
        });

        if (list.length === 0 && selected) {
            const type = selected === 'Battery' ? 'Battery Only' : selected;
            list.push({ productType: type, category: type });
        }
        if (list.length === 0) list.push({ productType: 'Solar', category: 'Solar' });

        return list;
    }, [opportunityDetails, productName]);

    const enableCheckCreditButton = React.useMemo(
        () => shouldShowCreditCheckStep(opportunityDetails),
        [opportunityDetails]
    );

    const creditAlreadyRun = React.useMemo(() => hasAnyCreditCheckRun(opportunityDetails), [opportunityDetails]);

    const checklistModalTitle = React.useMemo(() => {
        const base = `Finance Partner Sync — ${financePartnerSyncTitleSuffix(partnerName)}`;
        const selected = String(productName || '').trim();
        if (selected) {
            return `${base} (${selected})`;
        }
        if (!showProductTypesInTitle) return base;
        const types = getActiveProductTypeNamesForSyncPartner(opportunityDetails, partnerName);
        if (types.length > 1) {
            return `${base} (${types.join(', ')})`;
        }
        return base;
    }, [opportunityDetails, partnerName, productName, showProductTypesInTitle]);
    const effectiveStatus = React.useMemo(() => {
        // Keep creditCheck visible. IGS/Sunnova still use opportunity-level creditAlreadyRun.
        // LightReach with a product (Solar/Battery) uses that product's IsCreditPass only.
        // LightReach never gets a server-side title flag (the sync flow skips that call), so the
        // step is complete as soon as the sync ran — matching what the progress modal reports.
        const inheritOpportunityCredit = partnerName.toUpperCase() !== 'LIGHTREACH' || !productName;
        return {
            ...status,
            creditCheck: status.creditCheck || (inheritOpportunityCredit && creditAlreadyRun),
            titleCheck: status.titleCheck || (partnerSkipsTitleCheck(partnerName) && status.lead === true),
        };
    }, [status, creditAlreadyRun, partnerName, productName]);

    const automationSteps = config.requiredSteps.filter((k) => AUTOMATION_STEPS.includes(k));
    const additionalSteps = React.useMemo(() => {
        const base = config.requiredSteps.filter((k) => ADDITIONAL_STEPS.includes(k));
        // Do not hide the creditCheck row after a credit run; keep it visible and mark as completed.
        return base;
    }, [config.requiredSteps]);

    const shouldPulseSyncButton = React.useMemo(() => {
        const upName = partnerName.toUpperCase();
        if (upName !== 'IGS' && upName !== 'LIGHTREACH') return false;
        
        const hasSyncedLead = hasFinancePartnerSyncLeadForPartner(opportunityDetails, partnerName, productName);
        const isLeadCreated = status.lead || hasSyncedLead;
        return !isLeadCreated;
    }, [partnerName, productName, opportunityDetails, status]);

    return (
        <>
        <DashboardModal
            open={open}
            onClose={onClose}
            width={620}
            maskClosable={false}
            zIndex={OBD_FINANCE_PARTNER_SYNC_Z}
            theme={theme}
            className="obd-sync-modal obd-sync-modal--partner"
            rootClassName="obd-sync-modal-root obd-partner-sync-checklist-modal-root"
            title={checklistModalTitle}
            footer={(
                <Button type="primary" onClick={onClose}>
                    Done
                </Button>
            )}
            loading={showBusy}
            loadingMessage={finalBusyText}
        >
            <div style={{ position: 'relative' }} className={`obd-theme-${theme}`}>
                {noticeContextHolder}
                <div className="obd-sync-modal__content-body">
                    <p className="obd-sync-modal__subtitle">
                        Complete the sync steps below to ensure lead and utility records are correctly mapped in the partner portal.
                    </p>

                    {/* 1. Lead Sync — opens lead form (create new lead is in that modal footer) */}
                    <section className="obd-sync-modal__section">
                        <h4 className="obd-sync-modal__section-title">1. Lead Sync</h4>
                        <div className="obd-sync-modal__options obd-sync-modal__options--checkbox">
                            <button
                                type="button"
                                className={`obd-sync-modal__option ${status.leadSyncAction === 'sync_existing' ? 'obd-sync-modal__option--selected' : ''} ${shouldPulseSyncButton ? 'obd-sync-modal__option--attention' : ''}`}
                                onClick={handleOpenLeadSyncForm}
                            >
                                <User size={18} weight="duotone" className="obd-sync-modal__option-icon" />
                                <span className="obd-sync-modal__option-label">Sync with an existing lead</span>
                                {syncedLeadId ? (
                                    <span className="obd-sync-modal__option-lead-id">Lead Id {syncedLeadId}</span>
                                ) : null}
                            </button>
                        </div>
                    </section>

                    {/* 2. Automation Checks — partner-specific */}
                    {automationSteps.length > 0 && (
                        <section className="obd-sync-modal__section">
                            <h4 className="obd-sync-modal__section-title">2. Automation Checks</h4>
                            <ul className="obd-sync-modal__checklist">
                                {automationSteps.map((key) => (
                                    <li key={key} className="obd-sync-modal__check-item">
                                        <div className="obd-sync-modal__check-row-wrapper">
                                            <button type="button" className="obd-sync-modal__check-row" onClick={() => toggle(key)}>
                                                {effectiveStatus[key] ? (
                                                    <CheckCircle size={20} weight="fill" className="obd-sync-modal__check-icon obd-sync-modal__check-icon--done" />
                                                ) : (
                                                    <Circle size={20} weight="regular" className="obd-sync-modal__check-icon" />
                                                )}
                                                {STEP_ICONS[key]}
                                                <span>{getStepLabel(partnerName, key)}</span>
                                            </button>
                                            {key !== 'creditCheck' &&
                                                (partnerName.toUpperCase() === 'IGS' ||
                                                    partnerName.toUpperCase() === 'LIGHTREACH') && (
                                                <button
                                                    type="button"
                                                    className="obd-sync-modal__resync-btn"
                                                    title="Resync this item"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenProgress(key);
                                                    }}
                                                >
                                                    <ArrowsClockwise size={16} weight="bold" />
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* 3. Additional Required Syncs — partner-specific */}
                    {additionalSteps.length > 0 && (
                        <section className="obd-sync-modal__section">
                            <h4 className="obd-sync-modal__section-title">3. Additional Required Syncs</h4>
                            <ul className="obd-sync-modal__checklist">
                                {additionalSteps.map((key) => (
                                    <li key={key} className="obd-sync-modal__check-item">
                                        <div className="obd-sync-modal__check-row-wrapper">
                                            <button type="button" className="obd-sync-modal__check-row" onClick={() => toggle(key)}>
                                                {effectiveStatus[key] ? (
                                                    <CheckCircle size={20} weight="fill" className="obd-sync-modal__check-icon obd-sync-modal__check-icon--done" />
                                                ) : (
                                                    <Circle size={20} weight="regular" className="obd-sync-modal__check-icon" />
                                                )}
                                                {STEP_ICONS[key]}
                                                <span>{getStepLabel(partnerName, key)}</span>
                                            </button>
                                            {key !== 'creditCheck' &&
                                                (partnerName.toUpperCase() === 'IGS' ||
                                                    partnerName.toUpperCase() === 'LIGHTREACH') && (
                                                <button
                                                    type="button"
                                                    className="obd-sync-modal__resync-btn"
                                                    title="Resync this item"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenProgress(key); 
                                                    }}
                                                >
                                                    <ArrowsClockwise size={16} weight="bold" />
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </div>
            </div>
        </DashboardModal>

        {progressOpen &&
            (partnerName.toUpperCase() === 'IGS' || partnerName.toUpperCase() === 'LIGHTREACH') && (
            <FinancePartnerCreateLeadProgressModal
                open={progressOpen}
                onClose={async () => { 
                    setProgressOpen(false); 
                    setResyncStepId(null); 
                    
                    setIsBusy(true);
                    setBusyText('Refreshing Data...');
                    
                    try {
                        await refreshSyncStatus(false);
                        const upPartner = partnerName.toUpperCase();
                        if (upPartner === 'IGS' || upPartner === 'LIGHTREACH') {
                            await refreshOpportunityDetails(false);
                        }
                        if(upPartner === 'LIGHTREACH') await quoteServices.contractGetLoanStatusByOpportunity(opportunityId as string | number);
                    } finally {
                        setIsBusy(false);
                        setBusyText('Loading Sync Status...');
                    }
                }}
                opportunityId={String(opportunityId)}
                partnerName={partnerName}
                action={progressAction}
                products={productsList}
                resyncStepId={resyncStepId ?? undefined}
            />
        )}

        <CreditCheckModal />
        </>
    );
};

export default FinancePartnerChecklistModal;
